import { z } from 'zod';
/**
 * Tool Definition - Describes a tool that agents can use
 */
export const ToolParameterSchema = z.object({
    name: z.string(),
    type: z.enum(['string', 'number', 'boolean', 'object', 'array']),
    description: z.string(),
    required: z.boolean().default(false),
    schema: z.record(z.unknown()).optional(), // JSON Schema for complex types
});
export const ToolSchema = z.object({
    name: z.string(),
    description: z.string(),
    category: z.enum(['combat', 'exploration', 'roleplay', 'system', 'media']),
    parameters: z.array(ToolParameterSchema),
    // Execution constraints
    requiresDM: z.boolean().default(false),
    requiresApproval: z.boolean().default(false),
    rateLimit: z.object({
        maxCalls: z.number().int().positive(),
        windowSeconds: z.number().int().positive(),
    }).optional(),
    // JSON Schema for full validation
    jsonSchema: z.record(z.unknown()),
    // Examples for AI agents
    examples: z.array(z.object({
        description: z.string(),
        parameters: z.record(z.unknown()),
    })).optional(),
});
/**
 * Tool execution context
 */
export const ToolContextSchema = z.object({
    campaignId: z.string().uuid(),
    sceneId: z.string().uuid().optional(),
    userId: z.string(),
    agentId: z.string().optional(),
    // Current game state snapshot
    currentScene: z.record(z.unknown()).optional(),
    tokens: z.array(z.record(z.unknown())).optional(),
    // Permissions
    isDM: z.boolean().default(false),
    permissions: z.array(z.string()).default([]),
});
/**
 * Tool execution result
 */
export const ToolResultSchema = z.object({
    success: z.boolean(),
    data: z.unknown().optional(),
    error: z.string().optional(),
    proposalId: z.string().uuid().optional(), // If action requires proposal
    metadata: z.record(z.unknown()).optional(),
});
/**
 * Built-in tool definitions
 */
// Roll dice tool
export const rollDiceToolDefinition = {
    name: 'roll_dice',
    description: 'Roll dice using standard RPG notation (e.g., 2d6+3, 1d20)',
    category: 'combat',
    requiresDM: false,
    requiresApproval: false,
    parameters: [
        {
            name: 'expression',
            type: 'string',
            description: 'Dice expression (e.g., "2d6+3", "1d20")',
            required: true,
        },
        {
            name: 'reason',
            type: 'string',
            description: 'Why this roll is being made',
            required: false,
        },
        {
            name: 'hidden',
            type: 'boolean',
            description: 'Whether to hide the roll from players',
            required: false,
        },
    ],
    jsonSchema: {
        type: 'object',
        properties: {
            expression: { type: 'string', pattern: '^\\d+d\\d+([-+]\\d+)?$' },
            reason: { type: 'string' },
            hidden: { type: 'boolean' },
        },
        required: ['expression'],
    },
    examples: [
        {
            description: 'Roll for attack',
            parameters: { expression: '1d20+5', reason: 'Longsword attack' },
        },
        {
            description: 'Roll damage',
            parameters: { expression: '2d6+3', reason: 'Longsword damage' },
        },
    ],
};
// Spawn token tool
export const spawnTokenToolDefinition = {
    name: 'spawn_token',
    description: 'Create a new token on the map',
    category: 'combat',
    requiresDM: true,
    requiresApproval: true,
    parameters: [
        {
            name: 'name',
            type: 'string',
            description: 'Name of the token',
            required: true,
        },
        {
            name: 'type',
            type: 'string',
            description: 'Type of token (character, npc, object, marker)',
            required: true,
        },
        {
            name: 'position',
            type: 'object',
            description: 'Grid position {x, y}',
            required: true,
        },
        {
            name: 'count',
            type: 'number',
            description: 'Number of tokens to spawn',
            required: false,
        },
    ],
    jsonSchema: {
        type: 'object',
        properties: {
            name: { type: 'string' },
            type: { type: 'string', enum: ['character', 'npc', 'object', 'marker'] },
            position: {
                type: 'object',
                properties: { x: { type: 'number' }, y: { type: 'number' } },
                required: ['x', 'y'],
            },
            count: { type: 'number', minimum: 1 },
        },
        required: ['name', 'type', 'position'],
    },
};
// Move token tool
export const moveTokenToolDefinition = {
    name: 'move_token',
    description: 'Move a token to a new position',
    category: 'combat',
    requiresDM: false,
    requiresApproval: false,
    parameters: [
        {
            name: 'tokenId',
            type: 'string',
            description: 'UUID of the token to move',
            required: true,
        },
        {
            name: 'to',
            type: 'object',
            description: 'Target position {x, y}',
            required: true,
        },
    ],
    jsonSchema: {
        type: 'object',
        properties: {
            tokenId: { type: 'string', format: 'uuid' },
            to: {
                type: 'object',
                properties: { x: { type: 'number' }, y: { type: 'number' } },
                required: ['x', 'y'],
            },
        },
        required: ['tokenId', 'to'],
    },
};
// Tool registry
export const builtInTools = [
    rollDiceToolDefinition,
    spawnTokenToolDefinition,
    moveTokenToolDefinition,
];
//# sourceMappingURL=tools.js.map